import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { usePageMeta } from "@/hooks/usePageMeta";
import { rankingBoards } from "@/mocks/rankings";

export default function Rankings() {
  const [activeId, setActiveId] = useState(rankingBoards[0].id);
  const activeBoard = rankingBoards.find((board) => board.id === activeId) ?? rankingBoards[0];

  const jsonLd = useMemo(
    () => [
      {
        "@context": "https://schema.org",
        "@type": "CollectionPage",
        name: "盘点榜单",
        url: "https://yuntuizq.cn/rankings",
        description: "瓜集社整理的热门合集与盘点榜单。",
        inLanguage: "zh-CN",
      },
      {
        "@context": "https://schema.org",
        "@type": "ItemList",
        name: activeBoard.title,
        itemListElement: activeBoard.items.map((item) => ({
          "@type": "ListItem",
          position: item.rank,
          name: item.title,
          url: `https://yuntuizq.cn/topics/${item.topicId}`,
        })),
      },
      {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "首页", item: "https://yuntuizq.cn/" },
          {
            "@type": "ListItem",
            position: 2,
            name: "盘点榜单",
            item: "https://yuntuizq.cn/rankings",
          },
        ],
      },
    ],
    [activeBoard],
  );

  usePageMeta({
    title: "盘点榜单｜独家吃瓜盘点与热门合集排行 - 瓜集社",
    description:
      "独家吃瓜盘点榜单汇总本周热门合集、编辑部精选与最新上线专题，按阅读与收藏排序，快速找到值得看的合集目录。",
    keywords: "独家吃瓜盘点,合集排行,热门专题榜,精选盘点,榜单目录",
    path: "/rankings",
    jsonLd,
  });

  return (
    <>
      <section className="border-b border-background-200 bg-background-100/50">
        <div className="mx-auto max-w-[1440px] px-4 py-10 md:px-8 md:py-14">
          <nav aria-label="面包屑" className="mb-5 flex items-center gap-2 text-xs text-foreground-500">
            <Link to="/" className="transition-colors hover:text-primary-600">
              首页
            </Link>
            <i className="ri-arrow-right-s-line" />
            <span className="text-foreground-800">盘点榜单</span>
          </nav>
          <h1 className="font-heading text-2xl font-black text-foreground-950 md:text-4xl">
            盘点榜单
          </h1>
          <p className="mt-4 max-w-2xl text-sm leading-relaxed text-foreground-600 md:text-base">
            按热度、编辑精选与上线时间整理的三份榜单。每一条都能直接点进对应的独家吃瓜盘点专题，
            看完整条合集再决定值不值得收藏。
          </p>
        </div>
      </section>

      <section className="mx-auto max-w-[1440px] px-4 py-10 md:px-8 md:py-14">
        <div className="inline-flex rounded-full border border-background-300 bg-background-50 px-1 py-1">
          {rankingBoards.map((board) => (
            <button
              key={board.id}
              type="button"
              onClick={() => setActiveId(board.id)}
              className={`whitespace-nowrap rounded-full px-4 py-2 text-sm font-medium transition-colors ${
                activeId === board.id
                  ? "bg-foreground-950 text-background-50"
                  : "text-foreground-600 hover:text-foreground-950"
              }`}
            >
              {board.title}
            </button>
          ))}
        </div>

        <div className="mt-8 rounded-lg border border-background-200 bg-background-50">
          <div className="flex flex-wrap items-center justify-between gap-3 border-b border-background-200 px-5 py-5 md:px-7">
            <div>
              <h2 className="font-heading text-lg font-bold text-foreground-950 md:text-xl">
                {activeBoard.title}
              </h2>
              <p className="mt-1 text-sm text-foreground-600">{activeBoard.subtitle}</p>
            </div>
            <span className="rounded-full bg-secondary-100 px-3 py-1 text-xs text-secondary-900">
              更新于 {activeBoard.updated}
            </span>
          </div>

          <ol className="divide-y divide-background-200">
            {activeBoard.items.map((item) => (
              <li key={`${activeBoard.id}-${item.rank}`}>
                <div className="flex flex-col gap-4 px-5 py-5 transition-colors hover:bg-background-100/70 md:flex-row md:items-center md:px-7">
                  <span
                    className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-lg font-heading text-lg font-black ${
                      item.rank <= 3
                        ? "bg-accent-500 text-background-50"
                        : "bg-background-200 text-foreground-700"
                    }`}
                  >
                    {item.rank}
                  </span>
                  <div className="min-w-0 flex-1">
                    <Link
                      to={`/topics/${item.topicId}`}
                      className="font-heading text-base font-bold text-foreground-950 transition-colors hover:text-primary-600"
                    >
                      {item.title}
                    </Link>
                    <p className="mt-1 text-sm leading-relaxed text-foreground-600">{item.note}</p>
                  </div>
                  <div className="flex shrink-0 items-center gap-4">
                    <span className="whitespace-nowrap text-xs text-secondary-700">{item.metric}</span>
                    <Link
                      to={`/topics/${item.topicId}`}
                      className="flex h-9 w-9 items-center justify-center rounded-md border border-background-300 text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-700"
                      aria-label={`查看${item.title}`}
                    >
                      <i className="ri-arrow-right-line" />
                    </Link>
                  </div>
                </div>
              </li>
            ))}
          </ol>
        </div>
      </section>
    </>
  );
}